/**
 * Detects an expired/invalid-token failure across the two request paths this
 * app uses: `ApiError` from `@hqo/shared-modules` (REST via the api-client) and
 * `ApiRequestError` from `@hqo/service-booking-client` (SBS calls). Both expose
 * a numeric `statusCode`, and the raw-`fetch` QR query throws an error carrying
 * the same field, so a structural check keeps this decoupled from the concrete
 * error classes.
 */

// Direct file imports: `app/shared/auth`'s index re-exports the retry
// predicate, which imports this module.
import { authTokenStore } from 'app/shared/auth/auth-token-store';
import { isTokenStale, TOKEN_REFRESH_SKEW_MS } from 'app/shared/auth/jwt-expiry';

const UNAUTHORIZED_STATUS = 401;
const FORBIDDEN_STATUS = 403;

interface WithStatusCode {
  statusCode?: unknown;
}

const hasStatusCode = (error: unknown, statusCode: number): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  return (error as WithStatusCode).statusCode === statusCode;
};

export const isAuthError = (error: unknown): boolean => hasStatusCode(error, UNAUTHORIZED_STATUS);

export const isForbiddenError = (error: unknown): boolean => hasStatusCode(error, FORBIDDEN_STATUS);

/**
 * Whether a failure is worth one token refresh and retry: any 401, plus a 403
 * sent while the current token is stale by its own `exp`.
 *
 * The 403 branch is temporary. service-booking-service answers a rejected
 * token with 403 rather than 401 until SMB-1566 ships, after which it is dead
 * code. It keys on the token's `exp`, never on the response message, so a
 * genuine 403 (e.g. a user outside the event's audience) sent with a valid
 * token still fails fast.
 */
export const isRetryableAuthError = (error: unknown): boolean =>
  isAuthError(error)
  || (isForbiddenError(error) && isTokenStale(authTokenStore.peekToken(), TOKEN_REFRESH_SKEW_MS));
